import { pushDeviceId } from '@/lib/pwa/push'

import { notificationsApi, type WebPushSubscriptionPayload } from '../api'

const PUSH_KEY_STORAGE_KEY = 'kuniri.push.vapid.v1'

export type WebPushErrorCode = 'unsupported' | 'denied' | 'disabled' | 'error'

/**
 * Browser push failure with a catalog-mapped code. The message never contains
 * an endpoint, key or token: those values are sensitive and stay in memory.
 */
export class WebPushError extends Error {
  readonly code: WebPushErrorCode

  constructor(code: WebPushErrorCode) {
    super(code)
    this.name = 'WebPushError'
    this.code = code
  }
}

export function webPushSupported(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  )
}

export function pushPermission(): NotificationPermission | 'unsupported' {
  if (!webPushSupported()) return 'unsupported'
  return window.Notification.permission
}

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4)
  const normalized = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/')
  const raw = atob(normalized)
  const bytes = new Uint8Array(new ArrayBuffer(raw.length))
  for (let index = 0; index < raw.length; index += 1) {
    bytes[index] = raw.charCodeAt(index)
  }
  return bytes
}

/** Public VAPID key (not a secret); null when the server disables web push. */
export async function fetchPushPublicKey(
  signal?: AbortSignal,
): Promise<string | null> {
  const response = await notificationsApi.pushPublicKey(signal)
  const key = response.publicKey?.trim()
  return key ? key : null
}

async function ensureWorkerRegistration(): Promise<ServiceWorkerRegistration> {
  if (!webPushSupported()) throw new WebPushError('unsupported')
  try {
    // Always register the push worker. Returning an arbitrary existing root
    // registration could subscribe the browser to a worker without a `push`
    // handler (for example a legacy app-shell worker).
    return await navigator.serviceWorker.register('/push-sw.js', {
      scope: '/',
    })
  } catch {
    throw new WebPushError('error')
  }
}

function rememberVapidKey(publicKey: string): void {
  try {
    localStorage.setItem(PUSH_KEY_STORAGE_KEY, publicKey)
  } catch {
    // Private browsing can reject storage; the subscription still works.
  }
}

/**
 * Browsers do not expose a reliable cross-browser "which VAPID key is this
 * subscription for" API. Prefer the subscription options, then the key we
 * stored when creating it. If neither is available we re-subscribe once.
 */
function subscriptionMatchesVapidKey(
  subscription: PushSubscription,
  publicKey: string,
): boolean {
  try {
    const current = subscription.options?.applicationServerKey
    if (current) {
      const actual = new Uint8Array(current)
      const expected = urlBase64ToUint8Array(publicKey)
      if (actual.length !== expected.length) return false
      return actual.every((value, index) => value === expected[index])
    }
    return localStorage.getItem(PUSH_KEY_STORAGE_KEY) === publicKey
  } catch {
    return false
  }
}

export async function getExistingPushSubscription(): Promise<PushSubscription | null> {
  if (!webPushSupported()) return null
  try {
    const registration = await navigator.serviceWorker.getRegistration('/')
    if (!registration) return null
    return await registration.pushManager.getSubscription()
  } catch {
    return null
  }
}

function subscriptionPayload(
  subscription: PushSubscription,
): WebPushSubscriptionPayload {
  const json = subscription.toJSON()
  const p256dh = json.keys?.p256dh
  const auth = json.keys?.auth
  if (!json.endpoint || !p256dh || !auth) {
    throw new WebPushError('error')
  }
  return {
    endpoint: json.endpoint,
    expirationTime: json.expirationTime ?? null,
    keys: { p256dh, auth },
  }
}

/**
 * Enables web push only from an explicit user action.
 *
 * The permission prompt is the first thing this function can ask the user; no
 * mount effect or retry path may call it. A denied browser permission is
 * reported without requesting again, and a server with no VAPID key is a
 * clear disabled state rather than a broken subscribe.
 */
export async function enableWebPush(): Promise<string> {
  if (!webPushSupported()) throw new WebPushError('unsupported')
  if (window.Notification.permission === 'denied') {
    throw new WebPushError('denied')
  }

  // Check the server capability before prompting: a disabled deployment must
  // not ask for a permission it cannot use.
  let publicKey: string | null = null
  try {
    publicKey = await fetchPushPublicKey()
  } catch {
    throw new WebPushError('error')
  }
  if (!publicKey) throw new WebPushError('disabled')

  if (window.Notification.permission !== 'granted') {
    let permission: NotificationPermission = 'denied'
    try {
      permission = await window.Notification.requestPermission()
    } catch {
      permission = 'denied'
    }
    if (permission !== 'granted') throw new WebPushError('denied')
  }

  const registration = await ensureWorkerRegistration()
  let createdSubscription = false
  let subscription: PushSubscription
  try {
    let existing = await registration.pushManager.getSubscription()
    if (existing && !subscriptionMatchesVapidKey(existing, publicKey)) {
      // A key rotation (or a subscription inherited from the old app) cannot
      // be repaired without a new subscription.
      await existing.unsubscribe()
      existing = null
    }
    if (existing) {
      subscription = existing
    } else {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      })
      createdSubscription = true
    }
  } catch {
    throw new WebPushError('error')
  }

  const payload = subscriptionPayload(subscription)
  try {
    await notificationsApi.savePushSubscription(payload, pushDeviceId())
  } catch {
    if (createdSubscription) {
      try {
        await subscription.unsubscribe()
      } catch {
        // The API is the source of truth; a local orphan is harmless.
      }
    }
    throw new WebPushError('error')
  }
  rememberVapidKey(publicKey)

  return payload.endpoint
}

/**
 * Idempotent startup sync for an already-granted browser. It never prompts and
 * never creates a subscription where the user had none; it only:
 *
 * - re-registers the existing subscription with the API (the server row may
 *   have been lost in a deploy/database move),
 * - re-subscribes when the VAPID key changed (old endpoints are rejected by
 *   the push service).
 *
 * This is the recovery path for a PWA that shows "active" locally but receives
 * no pushes.
 */
let syncPromise: Promise<void> | null = null

export function syncExistingWebPushSubscription(): Promise<void> {
  if (syncPromise) return syncPromise
  const run = runExistingWebPushSync().finally(() => {
    syncPromise = null
  })
  syncPromise = run
  return run
}

async function runExistingWebPushSync(): Promise<void> {
  if (!webPushSupported() || window.Notification.permission !== 'granted') {
    return
  }
  try {
    const publicKey = await fetchPushPublicKey()
    if (!publicKey) return
    const registration = await ensureWorkerRegistration()
    let subscription = await registration.pushManager.getSubscription()
    if (!subscription) return
    if (!subscriptionMatchesVapidKey(subscription, publicKey)) {
      await subscription.unsubscribe()
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(publicKey),
      })
    }
    await notificationsApi.savePushSubscription(
      subscriptionPayload(subscription),
      pushDeviceId(),
    )
    rememberVapidKey(publicKey)
  } catch {
    // Startup sync is best-effort and must never block or prompt the app.
  }
}

/**
 * Removes this device's subscription only after the API accepted the deletion.
 * If the network fails the browser keeps receiving pushes, which is safer than
 * a server row without a matching browser endpoint.
 */
export async function disableWebPush(): Promise<void> {
  if (!webPushSupported()) throw new WebPushError('unsupported')
  const subscription = await getExistingPushSubscription()
  if (!subscription) return

  try {
    await notificationsApi.removePushSubscription(subscription.endpoint)
  } catch {
    throw new WebPushError('error')
  }

  try {
    await subscription.unsubscribe()
  } catch {
    throw new WebPushError('error')
  }
}
